import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Register = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    // Step 1: Persönliche Daten
    name: '',
    email: '',
    email_confirm: '',
    phone: '',
    password: '',
    password_confirm: '',
    // Step 2: Bankverbindung
    bank_account_holder: '',
    bank_iban: '',
    bank_bic: '',
    bank_name: '',
    // Step 3: Investition
    planned_investment_eur: '',
    planned_investment_eur_confirm: '',
    // Akzeptiert
    terms_accepted: false
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setError('');
  };

  const formatIBAN = (value) => {
    const cleaned = value.replace(/\s/g, '').toUpperCase();
    const groups = cleaned.match(/.{1,4}/g);
    return groups ? groups.join(' ') : cleaned;
  };

  const handleIBANChange = (e) => {
    const formatted = formatIBAN(e.target.value);
    setFormData(prev => ({ ...prev, bank_iban: formatted }));
  };

  const validateStep1 = () => {
    if (!formData.name.trim()) return 'Bitte geben Sie Ihren Namen ein';
    if (!formData.email.trim()) return 'Bitte geben Sie Ihre E-Mail-Adresse ein';
    if (formData.email !== formData.email_confirm) return 'Die E-Mail-Adressen stimmen nicht überein';
    if (!formData.phone.trim()) return 'Bitte geben Sie Ihre Telefonnummer ein';
    if (!formData.password) return 'Bitte wählen Sie ein Passwort';
    if (formData.password.length < 8) return 'Das Passwort muss mindestens 8 Zeichen lang sein';
    if (formData.password !== formData.password_confirm) return 'Die Passwörter stimmen nicht überein';
    return null;
  };

  const validateStep2 = () => {
    if (!formData.bank_account_holder.trim()) return 'Bitte geben Sie den Kontoinhaber ein';
    if (!formData.bank_iban.trim()) return 'Bitte geben Sie die IBAN ein';
    if (formData.bank_iban.replace(/\s/g, '').length < 15) return 'Die IBAN scheint ungültig zu sein';
    if (!formData.bank_bic.trim()) return 'Bitte geben Sie die BIC ein';
    if (!formData.bank_name.trim()) return 'Bitte geben Sie den Namen Ihrer Bank ein';
    return null;
  };

  const validateStep3 = () => {
    const amount = parseFloat(formData.planned_investment_eur);
    const amountConfirm = parseFloat(formData.planned_investment_eur_confirm);
    if (!amount || amount <= 0) return 'Bitte geben Sie eine gültige Investitionssumme ein';
    if (amount !== amountConfirm) return 'Die Investitionssummen stimmen nicht überein';
    if (!formData.terms_accepted) return 'Bitte akzeptieren Sie die Bedingungen';
    return null;
  };

  const handleNextStep = () => {
    let validationError = null;
    if (step === 1) validationError = validateStep1();
    if (step === 2) validationError = validateStep2();
    
    if (validationError) {
      setError(validationError);
      return;
    }
    setStep(step + 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationError = validateStep3();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/registration/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          email_confirm: formData.email_confirm,
          phone: formData.phone,
          password: formData.password,
          password_confirm: formData.password_confirm,
          bank_account_holder: formData.bank_account_holder,
          bank_iban: formData.bank_iban.replace(/\s/g, ''),
          bank_bic: formData.bank_bic.toUpperCase(),
          bank_name: formData.bank_name,
          planned_investment_eur: parseFloat(formData.planned_investment_eur),
          planned_investment_eur_confirm: parseFloat(formData.planned_investment_eur_confirm)
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Registrierung fehlgeschlagen');
      }

      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="max-w-lg w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Registrierung erfolgreich!</h1>
          <p className="text-gray-600 mb-6">
            Vielen Dank für Ihre Registrierung. Sie erhalten in Kürze eine E-Mail mit allen 
            Informationen zur Überweisung Ihrer Investitionssumme.
          </p>
          <p className="text-sm text-gray-500 mb-8">
            Sobald Ihr Geldeingang bestätigt wurde, wird Ihr Konto aktiviert und Sie können 
            sich im Investor Portal anmelden.
          </p>
          <Link 
            to="/" 
            className="inline-block px-6 py-3 bg-[#C5A059] text-white font-semibold rounded-lg hover:bg-[#B08E4A] transition-colors"
          >
            Zur Startseite
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-[#0A0A0A] py-6">
        <div className="max-w-4xl mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_29d23b9a-c758-4d5b-a3ec-a84a3acd0754/artifacts/883asqj8_image.png" 
              alt="Value Plus" 
              className="h-10 w-auto"
            />
            <div>
              <h1 className="text-white font-bold">Investor Portal</h1>
              <p className="text-gray-400 text-sm">Value Plus GmbH</p>
            </div>
          </div>
          <Link to="/" className="text-[#C5A059] hover:text-[#D4B068] text-sm font-medium">
            Bereits registriert? Anmelden
          </Link>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          {[1, 2, 3].map((s) => (
            <React.Fragment key={s}>
              <div 
                className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-colors ${
                  step >= s ? 'bg-[#C5A059] text-white' : 'bg-gray-200 text-gray-500'
                }`}
              >
                {s}
              </div>
              {s < 3 && (
                <div className={`w-16 h-1 mx-2 ${step > s ? 'bg-[#C5A059]' : 'bg-gray-200'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        <div className="text-center mb-6">
          <p className="text-gray-500 text-sm">
            {step === 1 && 'Schritt 1 von 3: Persönliche Daten'}
            {step === 2 && 'Schritt 2 von 3: Bankverbindung'}
            {step === 3 && 'Schritt 3 von 3: Investitionssumme'}
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            {step === 1 && 'Ihre persönlichen Daten'}
            {step === 2 && 'Ihre Bankverbindung'}
            {step === 3 && 'Ihre Investition'}
          </h2>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Step 1: Personal Data */}
            {step === 1 && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vollständiger Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C5A059] focus:border-transparent"
                    placeholder="Max Mustermann"
                    data-testid="register-name"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">E-Mail-Adresse *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C5A059] focus:border-transparent"
                      placeholder="ihre@email.de"
                      data-testid="register-email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">E-Mail bestätigen *</label>
                    <input
                      type="email"
                      name="email_confirm"
                      value={formData.email_confirm}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C5A059] focus:border-transparent"
                      placeholder="ihre@email.de"
                      data-testid="register-email-confirm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telefonnummer *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C5A059] focus:border-transparent"
                    placeholder="+49 123 456789"
                    data-testid="register-phone"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Passwort *</label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C5A059] focus:border-transparent"
                      placeholder="Mind. 8 Zeichen"
                      data-testid="register-password"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Passwort bestätigen *</label>
                    <input
                      type="password"
                      name="password_confirm"
                      value={formData.password_confirm}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C5A059] focus:border-transparent"
                      placeholder="Passwort wiederholen"
                      data-testid="register-password-confirm"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Bank Details */}
            {step === 2 && (
              <div className="space-y-4">
                <p className="text-sm text-gray-600 mb-4">
                  Bitte geben Sie Ihre Bankverbindung an, auf die Ihre Zinszahlungen überwiesen werden sollen.
                </p>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Kontoinhaber *</label>
                  <input
                    type="text"
                    name="bank_account_holder"
                    value={formData.bank_account_holder}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C5A059] focus:border-transparent"
                    placeholder="Max Mustermann"
                    data-testid="register-bank-holder"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">IBAN *</label>
                  <input
                    type="text"
                    name="bank_iban"
                    value={formData.bank_iban}
                    onChange={handleIBANChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C5A059] focus:border-transparent font-mono"
                    placeholder="DE89 3704 0044 0532 0130 00"
                    data-testid="register-bank-iban"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">BIC *</label>
                    <input
                      type="text"
                      name="bank_bic"
                      value={formData.bank_bic}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C5A059] focus:border-transparent font-mono uppercase"
                      placeholder="COBADEFFXXX"
                      data-testid="register-bank-bic"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bank *</label>
                    <input
                      type="text"
                      name="bank_name"
                      value={formData.bank_name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C5A059] focus:border-transparent"
                      placeholder="Commerzbank"
                      data-testid="register-bank-name"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Investment */}
            {step === 3 && (
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-4">Unsere Anlageprodukte</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-white rounded-lg border">
                      <span className="font-medium">BASIS</span>
                      <span className="text-[#C5A059] font-bold">5,0% p.a.</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-white rounded-lg border">
                      <span className="font-medium">ERHÖHT</span>
                      <span className="text-[#C5A059] font-bold">6,5% p.a.</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-white rounded-lg border">
                      <span className="font-medium">PREMIUM</span>
                      <span className="text-[#C5A059] font-bold">9,7% p.a.</span>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 mt-4">
                    Das passende Produkt wird basierend auf Ihrer Investitionssumme von unserem Team ausgewählt.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Investitionssumme (EUR) *</label>
                    <input
                      type="number"
                      name="planned_investment_eur"
                      value={formData.planned_investment_eur}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C5A059] focus:border-transparent"
                      placeholder="z.B. 50000"
                      min="0"
                      step="100"
                      data-testid="register-investment"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Summe bestätigen *</label>
                    <input
                      type="number"
                      name="planned_investment_eur_confirm"
                      value={formData.planned_investment_eur_confirm}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C5A059] focus:border-transparent"
                      placeholder="Summe wiederholen"
                      min="0"
                      step="100"
                      data-testid="register-investment-confirm"
                    />
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    name="terms_accepted"
                    checked={formData.terms_accepted}
                    onChange={handleChange}
                    className="mt-1 h-5 w-5 text-[#C5A059] border-gray-300 rounded focus:ring-[#C5A059]"
                    data-testid="register-terms"
                  />
                  <label className="text-sm text-gray-600">
                    Ich habe die Informationen zur Kenntnis genommen und möchte als Investor bei Value Plus registriert werden. 
                    Mir ist bewusst, dass mein Konto erst nach Eingang meiner Investitionssumme aktiviert wird.
                  </label>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8">
              {step > 1 && (
                <button
                  type="button"
                  onClick={() => setStep(step - 1)}
                  className="px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Zurück
                </button>
              )}
              
              {step < 3 ? (
                <button
                  type="button"
                  onClick={handleNextStep}
                  className="ml-auto px-8 py-3 bg-[#C5A059] text-white font-semibold rounded-lg hover:bg-[#B08E4A] transition-colors"
                  data-testid="register-next"
                >
                  Weiter
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={isLoading}
                  className="ml-auto px-8 py-3 bg-[#C5A059] text-white font-semibold rounded-lg hover:bg-[#B08E4A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  data-testid="register-submit"
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      <span>Wird gesendet...</span>
                    </>
                  ) : (
                    <span>Registrierung abschließen</span>
                  )}
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-gray-500 mt-6">
          © {new Date().getFullYear()} Value Plus GmbH. Alle Rechte vorbehalten.
        </p>
      </div>
    </div>
  );
};

export default Register;
